import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Checkbox } from "@mui/material";

import { QuizSection } from "../shared/QuizSection";
import { StyledTextField } from "../form/StyledTextField";
import { DogForm } from "@/helpers/types";
import { useQuizContext } from "@/state/QuizContext";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  dogs: { id: string; form: any }[];
  updateDog: (dogId: string, update: any) => void;
};

export default function StepOwner({ dogs, updateDog }: Props) {
  const { state } = useQuizContext();

  const form: Partial<DogForm> = dogs[0]?.form ?? {};

  const existingOwnerData = useMemo(() => {
    for (const dogId of state.dogOrder) {
      const dogForm = state.dogForms[dogId];
      if (dogForm?.ownerName || dogForm?.ownerEmail) {
        return {
          ownerName: dogForm.ownerName || "",
          ownerEmail: dogForm.ownerEmail || "",
          ownerPhone: dogForm.ownerPhone || "",
          marketingOptIn: dogForm.marketingOptIn ?? false,
        };
      }
    }
    return null;
  }, [state.dogOrder, state.dogForms]);

  const ownerName = form.ownerName || existingOwnerData?.ownerName || "";
  const ownerEmail = form.ownerEmail || existingOwnerData?.ownerEmail || "";
  const ownerPhone = form.ownerPhone || existingOwnerData?.ownerPhone || "";
  // Issue #53: consent defaults to ticked (client decision — compliance risk
  // logged on the ticket). An explicit untick is always respected.
  const marketingOptIn = form.marketingOptIn ?? existingOwnerData?.marketingOptIn ?? true;

  const firstDogName = dogs[0]?.form?.name?.trim() || "your dog";

  const [localName, setLocalName] = useState(ownerName);
  const [localEmail, setLocalEmail] = useState(ownerEmail);
  const [localPhone, setLocalPhone] = useState(ownerPhone);

  useEffect(() => setLocalName(ownerName), [ownerName]);
  useEffect(() => setLocalEmail(ownerEmail), [ownerEmail]);
  useEffect(() => setLocalPhone(ownerPhone), [ownerPhone]);

  // Sync owner data to ALL dogs
  const updateAllDogs = (update: Partial<DogForm>) => {
    for (const dog of dogs) {
      updateDog(dog.id, update);
    }
  };

  // Pre-fill from existing data on mount — sync to ALL dogs so per-dog
  // validation never trips on a dog that missed the owner fields.
  useEffect(() => {
    if (existingOwnerData && !form.ownerName && !form.ownerEmail) {
      updateAllDogs(existingOwnerData);
    }
  }, []);

  // Issue #53: hydrated pre-change saves can carry no marketingOptIn at all —
  // commit the displayed pre-ticked default so the stored value and the
  // checkbox the customer saw can never disagree.
  useEffect(() => {
    if (form.marketingOptIn == null && existingOwnerData?.marketingOptIn == null) {
      updateAllDogs({ marketingOptIn: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Owner fields: left-aligned text; size/padding/font come from the shared
  // StyledTextField default so they match the breed field exactly.
  const ownerField = {
    "& .MuiOutlinedInput-root input": {
      textAlign: "left",
    },
  } as const;

  return (
    <QuizSection>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: 500,
          mx: "auto",
          width: "100%",
          gap: { xs: "16px", md: "20px" },
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: QUIZ.font.display,
            fontWeight: 900,
            textTransform: "uppercase",
            color: QUIZ.color.ink,
            fontSize: { xs: "20px", md: "41px" },
            lineHeight: { xs: 1.2, md: 1 },
          }}
        >
          Almost there!
        </Typography>

        <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 16, md: 18 }, color: QUIZ.color.grey }}>
          Tell us where to send your personalised plan
        </Typography>

        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: { xs: "24px", md: "24px" }, mt: "4px" }}>
          <StyledTextField
            fullWidth
            placeholder="Full name"
            value={localName}
            onChange={(e) => {
              const v = e.target.value;
              setLocalName(v);
              updateAllDogs({ ownerName: v });
            }}
            autoComplete="off"
            inputProps={{ autoCapitalize: "words", autoComplete: "off" }}
            sx={ownerField}
          />
          <StyledTextField
            fullWidth
            placeholder="Email address"
            value={localEmail}
            onChange={(e) => {
              const v = e.target.value;
              setLocalEmail(v);
              updateAllDogs({ ownerEmail: v });
            }}
            autoComplete="off"
            inputProps={{ autoComplete: "off" }}
            inputMode="email"
            type="email"
            sx={ownerField}
          />
          <StyledTextField
            fullWidth
            placeholder="Phone number (optional)"
            value={localPhone}
            onChange={(e) => {
              const v = e.target.value;
              setLocalPhone(v);
              updateAllDogs({ ownerPhone: v });
            }}
            autoComplete="off"
            inputProps={{ autoComplete: "off" }}
            inputMode="tel"
            type="tel"
            sx={ownerField}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontSize: { xs: 13, md: 14 },
            color: QUIZ.color.grey,
            lineHeight: 1.4,
          }}
        >
          We'll email your personalised nutrition plan and important updates.
        </Typography>

        {/* Marketing opt-in — Figma: left-aligned with the fields, 16px checkbox */}
        <Box
          onClick={() => updateAllDogs({ marketingOptIn: !marketingOptIn })}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: "12px",
            cursor: "pointer",
            userSelect: "none",
            mt: { xs: "4px", md: "6px" },
          }}
        >
          <Checkbox
            checked={marketingOptIn === true}
            onChange={(e) => updateAllDogs({ marketingOptIn: e.target.checked })}
            sx={{
              p: 0,
              mt: "2px",
              color: QUIZ.color.cream3,
              "& .MuiSvgIcon-root": { fontSize: { xs: 20, md: 21 } },
              "&.Mui-checked": { color: QUIZ.color.brown },
            }}
          />
          <Box sx={{ textAlign: "left" }}>
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: 15, md: 15 },
                color: QUIZ.color.ink,
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              Yes, email me {firstDogName}'s feeding tips, exclusive offers and more.
            </Typography>
            <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: 13, color: QUIZ.color.grey }}>
              You'll get {firstDogName}'s plan either way, and you can unsubscribe in a tap.
            </Typography>
          </Box>
        </Box>
      </Box>
    </QuizSection>
  );
}
